/**
 * GlobalHeader.tsx
 * Primary top navigation bar with hamburger menu, logo, search, notifications, and user menu
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { User, adminService } from '../../services/auth';
import { Menu, Search, Bell, X, FileText, Receipt, Users } from 'lucide-react';
import { theme, injectScrollbarStyles, Kbd } from '@turn-connect/ui';
import { useResponsive, hasPermission, isSystemAdmin as isSystemAdminCheck } from '@turn-connect/hooks';
import { WorkSessionClock, ResourceLink, describeActivity, ActivityDetailModal } from '@turn-connect/shared';
import type { Activity } from '@turn-connect/shared';
import HamburgerMenu from './HamburgerMenu';
import { HeaderContainer } from '../layouts/HeaderContainer';
import UserProfileDropdown from '../UserProfileDropdown';
import { WorkOrderSummary, workOrdersService } from '../../services/workOrders';
import { invoicingService } from '../../services/admin/invoicingService';
import type { InvoiceBatch } from '@turn-connect/shared';
import { CUSTOMER_KIND_LABELS, type Customer } from '@turn-connect/shared';
import { customerSearchDetail, searchCustomersAt, useCustomerSearchOn } from '../../features/customers/customerSearch';
import AIChatButton from '../aichat/AIChatButton';
import AIChatPanel from '../aichat/AIChatPanel';
import { getWorkOrderStatusColor, getWorkOrderStatusLabel } from '@turn-connect/utils';
import { BrandLogo } from '../ui/BrandLogo';
import { GLOBAL_SEARCH_HINT, GLOBAL_SEARCH_HINT_SHORT, GLOBAL_SEARCH_MIN_CHARS } from '../../utils/globalSearch';
import { useSettingsSearch, type SettingsSearchHit } from '../../hooks/useSettingsSearch';
import { openSettings, type SettingsTarget } from '../../utils/settingsDialogs';
import { useSettingsEntry } from '../../hooks/useSettingsEntry';
import SettingsSearchRow from './SettingsSearchRow';

/** Customers shown at most in the search dropdown (before work orders and invoices). */
const CUSTOMER_RESULTS = 3;

// Inject scrollbar styles
if (typeof document !== 'undefined') {
  injectScrollbarStyles(theme.scrollbars.searchResults, 'search-results');
  injectScrollbarStyles(theme.scrollbars.activitiesList, 'activities-list');
}

// Translucent badge background derived from a solid hex color
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Status colors for work order search results (canonical palette from @turn-connect/utils)
// eslint-disable-next-line no-restricted-syntax -- derives a bg/text pair from the shared work-order palette (getWorkOrderStatusColor)
const getStatusColor = (status: string): { bg: string; text: string } => {
  const text = getWorkOrderStatusColor(status);
  return { bg: hexToRgba(text, 0.15), text };
};

// Invoice-batch search results keep their existing neutral badge for all batch
// statuses (pending/sending/sent/failed) — only the work-order palette was
// centralized into @turn-connect/utils.
const BATCH_STATUS_BADGE = { bg: 'rgba(156, 163, 175, 0.15)', text: '#9CA3AF' };

// Format status label for display
const formatStatusLabel = (status: string): string => (status ? getWorkOrderStatusLabel(status) : 'Unknown');

// Platform detection for the command-palette keycap hint (⌘K vs Ctrl K)
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform || '');

// Discriminated union for the multi-source search dropdown. The parent
// decides where to navigate based on `type` — work orders open in the
// work-orders view, invoice batches open the batch detail modal inside the
// Reports view. Accounts, locations and settings sections open a settings
// dialog over the current view.
export type SearchSelection =
  | { type: 'work_order'; id: string }
  | { type: 'customer'; id: string; locationId: string }
  | { type: 'invoice_batch'; id: string; displayId: string }
  | { type: 'settings'; target: SettingsTarget };

type SearchResultItem =
  | { type: 'work_order'; data: WorkOrderSummary }
  | { type: 'customer'; data: Customer }
  | { type: 'invoice_batch'; data: InvoiceBatch }
  | { type: 'settings'; data: SettingsSearchHit };

interface GlobalHeaderProps {
  user: User;
  permissions: string[];
  onLogout: () => void;
  onEditProfile: () => void;
  onAdminDashboard?: () => void;
  onSearchSelect?: (selection: SearchSelection) => void;
  locationId?: string | null;
  /** The account in scope: what the user menu's Settings and settings search results open. */
  scopeAccountId?: string | null;
  /**
   * The logo: the person's default dashboard, every time — leaving any open
   * settings page first (asking about unsaved edits). Without it the logo only
   * asks the app to show the dashboard.
   */
  onLogoClick?: () => void;
  customSearchIcon?: React.ReactNode;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  user,
  permissions,
  onLogout,
  onEditProfile,
  onAdminDashboard,
  onSearchSelect,
  locationId,
  scopeAccountId = null,
  customSearchIcon,
  onLogoClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [serverSuggestions, setSuggestions] = useState<SearchResultItem[]>([]);
  const [openSuggestions, setOpenSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesTotal, setActivitiesTotal] = useState<number>(0);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  // The feed row the user opened — shown in the shared activity detail modal,
  // the same formatted view every other activity list opens.
  const [openedActivity, setOpenedActivity] = useState<Activity | null>(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  // Desktop: the search sits folded into a Search button until it is used, so
  // pages with their own search (Executive's Find a location) show one box.
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  // Escape folds the box back; focus then returns to the Search button.
  const refocusSearchButton = useRef(false);
  useEffect(() => {
    if (!desktopSearchOpen && refocusSearchButton.current) {
      refocusSearchButton.current = false;
      searchButtonRef.current?.focus();
    }
  }, [desktopSearchOpen]);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatStreaming] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const activitiesLoadedRef = useRef<boolean>(false);

  // Responsive hooks
  const { isMobile, isTablet } = useResponsive();

  const ICON_SIZE = isMobile ? 22 : 20;

  // Rem-based sizing for font-scaling support
  const headerHeight = isMobile ? '3.5rem' : '3.75rem'; // 56px / 60px at default
  // A phone has ~360–390px for seven items; at 0.5rem between them the
  // profile button was pushed off the right edge.
  const headerGap = isMobile ? '0.25rem' : '1rem';
  const btnSize = isMobile ? '2.75rem' : '2.5rem'; // 44px / 40px at default
  // The search grows to this when the bar has room and gives way first when
  // it does not (the spacer before it takes the rest).
  const searchWidth = isTablet ? '18rem' : '32rem'; // 288px / 512px at default
  const searchHeight = '2.25rem'; // 36px at default

  // Determine if user has admin permissions
  const isSystemAdmin = useMemo(() => isSystemAdminCheck(permissions) || user?.email === 'sysadmin@turnconnect.demo', [permissions, user]);

  // Check if hamburger menu has any content to show. My Time moved into the
  // My Work nav tab, so the menu is Platform Administration only again.
  const hasHamburgerContent = useMemo(() => {
    return isSystemAdmin && !!onAdminDashboard;
  }, [isSystemAdmin, onAdminDashboard]);

  // Check if user can clock in (has work_sessions:create permission)
  const canClockIn = useMemo(() => {
    return (permissions || []).includes('work_sessions:create');
  }, [permissions]);

  // Geofence bypass: never block these users client-side — the server allows
  // them through the fence (timeclock:bypass_geofence / system:admin).
  const bypassGeofence = useMemo(() => {
    const perms = permissions || [];
    return perms.includes('timeclock:bypass_geofence') || isSystemAdminCheck(perms);
  }, [permissions]);

  // Account-wide activities for the account in the CURRENT token. Distinct from
  // activities:read_all, which crosses every account and is system-admin only —
  // seeding read_all onto per-account default roles is what leaked other
  // tenants' audit rows into admins' notification panels.
  const hasActivitiesReadAccount = useMemo(() => {
    return (permissions || []).includes('activities:read:account');
  }, [permissions]);

  const hasActivitiesRead = useMemo(() => {
    return (permissions || []).includes('activities:read');
  }, [permissions]);

  // NOTE: the local isSuperAdmin/isAccountAdmin heuristics that used to live
  // here (substring scans over role names and lowercased permission spellings)
  // are gone — activity scope is decided by explicit permission keys now, which
  // is both narrower and checkable against the backend gate. isSystemAdmin
  // above is the canonical @turn-connect/hooks check.

  const accountId = (user as any)?.account_id || (user as any)?.accounts?.[0]?.id;
  // Customers in the search only where the account uses them and the person may see them.
  const customerSearchOn = useCustomerSearchOn(scopeAccountId || accountId, permissions || []);

  // Check if user can use AI chat
  const canUseAIChat = useMemo(() => hasPermission(permissions, 'ai:chat'), [permissions]);

  const isActivitiesAvailable = useMemo(() => {
    // Mirrors the branches in loadActivities: system admin (all accounts),
    // activities:read:account (this account), or activities:read (this location).
    if (isSystemAdmin) return true;
    if (hasActivitiesReadAccount && accountId) return true;
    if (hasActivitiesRead && locationId) return true;
    return false;
  }, [isSystemAdmin, hasActivitiesReadAccount, hasActivitiesRead, accountId, locationId]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setOpenSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, []);

  // Close notifications when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, []);

  // Use locationId prop or fall back to user's location_id
  const effectiveLocationId = locationId || (user as any)?.location_id || null;

  // Settings: the user menu item opens the account in scope (or, without
  // account access, the location); the same checks as the Executive dashboard.
  const settingsTarget = useSettingsEntry(user as unknown as Record<string, unknown>, permissions, {
    accountId: scopeAccountId,
    locationId: effectiveLocationId,
  });

  // Accounts, locations and settings sections ride along with the work order
  // and invoice results: sections from the first character, the rest from two.
  const { hits: settingsHits, loading: settingsLoading } = useSettingsSearch(searchValue, {
    permissions,
    accountId: scopeAccountId,
    locationId: effectiveLocationId,
  });
  const suggestions = useMemo<SearchResultItem[]>(
    () => [...serverSuggestions, ...settingsHits.map((data) => ({ type: 'settings' as const, data }))],
    [serverSuggestions, settingsHits]
  );
  useEffect(() => {
    if (settingsHits.length > 0 && searchValue.trim()) setOpenSuggestions(true);
  }, [settingsHits, searchValue]);

  // Debounced search across work orders + invoice batches.
  //
  // We fan out to both endpoints in parallel and interleave the results so a
  // typed batch display id (MA-0447) and a VIN both surface in the same
  // dropdown. When a location is selected the work-order search stays
  // location-scoped; when none is selected it runs multi-account (no
  // location_id sent), so the backend scopes by the caller's accessible
  // accounts — mirroring the account-scoped invoice-batch search. That means
  // the "select a location" empty state only fires when *both* sources return
  // nothing.
  useEffect(() => {
    let cancelled = false;
    const term = (searchValue || '').trim();

    // Too short to be worth a search (see GLOBAL_SEARCH_MIN_CHARS).
    if (term.length < GLOBAL_SEARCH_MIN_CHARS) {
      setSuggestions([]);
      setOpenSuggestions(false);
      // A search this effect cancelled cannot clear its own spinner.
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const handle = setTimeout(async () => {
      // Cap each source at half the dropdown so neither type can hide the other.
      const perTypeCap = 5;
      // Universal search: always search the caller's FULL accessible scope
      // (every account/location a sysadmin or multi-account user can reach),
      // never just the selected location — the backend scopes by accessible
      // accounts when no location_id is passed. A partner job's work order in
      // one account must be findable without first selecting that location.
      const woPromise: Promise<WorkOrderSummary[]> = workOrdersService
        .searchWorkOrders(undefined, term, perTypeCap, true, true)
        .catch((err) => {
          console.error('Search error (work orders):', err);
          return [];
        });
      const batchPromise: Promise<InvoiceBatch[]> = invoicingService.searchInvoiceBatches(term, perTypeCap).catch((err) => {
        console.error('Search error (invoice batches):', err);
        return [];
      });

      // Customers at this store (accounts using Customers), listed first.
      const customerPromise = customerSearchOn ? searchCustomersAt(effectiveLocationId, term, CUSTOMER_RESULTS) : Promise.resolve([]);

      try {
        const [workOrders, batches, customers] = await Promise.all([woPromise, batchPromise, customerPromise]);
        if (cancelled) return;
        // Interleave so both types are visible even when one source is hot.
        const woItems: SearchResultItem[] = (workOrders || []).map((w) => ({ type: 'work_order', data: w }));
        const batchItems: SearchResultItem[] = (batches || []).map((b) => ({ type: 'invoice_batch', data: b }));
        const merged: SearchResultItem[] = [];
        const max = Math.max(woItems.length, batchItems.length);
        for (let i = 0; i < max; i++) {
          if (i < woItems.length) merged.push(woItems[i]);
          if (i < batchItems.length) merged.push(batchItems[i]);
        }
        const customerItems: SearchResultItem[] = customers.map((c) => ({ type: 'customer', data: c }));
        const finalSuggestions = [...customerItems, ...merged.slice(0, 8 - customerItems.length)];
        setSuggestions(finalSuggestions);
        setOpenSuggestions(true);
        // Pre-highlight the top row so it's visible which result Enter opens.
        setHighlightIndex(finalSuggestions.length > 0 ? 0 : -1);
      } catch (err) {
        console.error('Search error:', err);
        if (!cancelled) {
          setSuggestions([]);
          setOpenSuggestions(false);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchValue, effectiveLocationId, customerSearchOn]);

  // Load activities when notifications opened
  useEffect(() => {
    if (showNotifications && !activitiesLoadedRef.current && isActivitiesAvailable) {
      loadActivities();
    }
  }, [showNotifications, isActivitiesAvailable]);

  const loadActivities = async () => {
    if (activitiesLoadedRef.current) return;
    activitiesLoadedRef.current = true;
    setActivitiesLoading(true);
    setActivitiesError(null);

    try {
      let response: { activities: any[]; total: number };

      // Widest scope first, and system admin is the ONLY route to the
      // cross-account feed — the backend now refuses it for anyone else.
      if (isSystemAdmin) {
        // Across all accounts
        response = await adminService.getAllActivities(20);
      } else if (hasActivitiesReadAccount && accountId) {
        // Every location in the caller's current account
        response = await adminService.getAccountActivities(accountId, 20);
      } else if (effectiveLocationId) {
        // Location users load location-scoped activities
        response = await adminService.getLocationActivities(effectiveLocationId, 20);
      } else {
        // No access - shouldn't reach here due to isActivitiesAvailable check
        setActivitiesError('No activities access');
        return;
      }

      const activitiesList = Array.isArray(response.activities) ? response.activities : [];
      setActivities(activitiesList);
      setActivitiesTotal(response.total || activitiesList.length);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setActivitiesError('Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const selectionFromItem = (item: SearchResultItem): SearchSelection => {
    if (item.type === 'invoice_batch') {
      return { type: 'invoice_batch', id: item.data.id, displayId: item.data.display_id || '' };
    }
    if (item.type === 'settings') return { type: 'settings', target: item.data.target };
    if (item.type === 'customer') return { type: 'customer', id: item.data.id, locationId: effectiveLocationId ?? '' };
    return { type: 'work_order', id: item.data.id };
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Default to the first result so a plain type-then-Enter opens the top
      // match; arrow keys still let the user pick a different row first.
      const idx = highlightIndex >= 0 ? highlightIndex : 0;
      if (suggestions[idx]) {
        handleSuggestionClick(suggestions[idx]);
      }
    } else if (e.key === 'Escape') {
      setOpenSuggestions(false);
    }
  };

  const handleSuggestionClick = (item: SearchResultItem) => {
    setOpenSuggestions(false);
    setDesktopSearchOpen(false);
    setSearchValue('');
    setSuggestions([]);
    if (onSearchSelect) {
      onSearchSelect(selectionFromItem(item));
    }
  };

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: theme.colors.background.secondary,
          borderBottom: `1px solid ${theme.colors.border.default}`,
        }}
      >
        <HeaderContainer style={{ minHeight: headerHeight, gap: headerGap }}>
          {/* Hamburger Menu - only show if there's content for the user */}
          {hasHamburgerContent && (
            <button
              onClick={() => setIsMenuOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: btnSize,
                height: btnSize,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                color: theme.colors.text.primary,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label="Menu"
            >
              <Menu size={ICON_SIZE} />
            </button>
          )}

          {/* Mobile Search Button - Left of Logo */}
          {isMobile && (
            <button
              onClick={() => setShowMobileSearch(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: btnSize,
                height: btnSize,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                color: theme.colors.text.primary,
                cursor: 'pointer',
                flexShrink: 0,
              }}
              aria-label="Search"
            >
              <Search size={ICON_SIZE} />
            </button>
          )}

          {/* Logo — on a phone it may shrink a little rather than push the
              profile button off screen (a 360px Android has no room to spare). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              ...(isMobile ? { flex: '0 1 8rem', minWidth: '5rem' } : { flexShrink: 0 }),
            }}
          >
            {/* BrandLogo, not a bare <img>: it carries the WebP source and the
                intrinsic width/height that keep the header from shifting while
                the image loads. The old inline copy also patched a text
                fallback into the DOM by hand on error — outside React, so it
                could stack up and was never cleaned up. */}
            <button
              type="button"
              aria-label="Go to dashboard"
              onClick={() => {
                if (onLogoClick) onLogoClick();
                else window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'dashboard' } }));
              }}
              // A real button: the span this replaced took clicks but was not
              // focusable and ignored Enter/Space.
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                ...(isMobile ? { width: '100%' } : {}),
              }}
            >
              {isMobile ? (
                <BrandLogo themeAware height={20} style={{ width: '100%', height: 'auto', maxHeight: 20 }} />
              ) : (
                <BrandLogo themeAware height={24} />
              )}
            </button>
          </div>

          {/* Spacer to push notifications/user to the right */}
          <div style={{ flex: 1, minWidth: 0 }} />

          {/* Desktop Search: a Search button that opens into the search box */}
          {!isMobile && !desktopSearchOpen && !searchValue && (
            <button
              ref={searchButtonRef}
              type="button"
              onClick={() => setDesktopSearchOpen(true)}
              // Named by its visible text (the ⌘K chip is hidden from it).
              aria-label={GLOBAL_SEARCH_HINT}
              data-testid="header-search-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                height: searchHeight,
                padding: '0 0.75rem',
                backgroundColor: theme.colors.background.primary,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: '0.5rem',
                color: theme.colors.text.secondary,
                fontSize: '0.75rem',
                cursor: 'pointer',
                // As wide as the open box when there is room, so it reads as
                // the search box it opens into; never narrower than "Search ⌘K".
                flex: `0 1 ${searchWidth}`,
                minWidth: '8.5rem',
              }}
            >
              <Search size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
              {/* The hint says what it finds; it trims to "Search wo…" as the bar narrows. */}
              <span
                data-testid="header-search-hint"
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}
              >
                {GLOBAL_SEARCH_HINT}
              </span>
              <span aria-hidden="true" style={{ flexShrink: 0 }}>
                <Kbd style={{ color: theme.colors.text.tertiary }}>{IS_MAC ? '⌘K' : 'Ctrl K'}</Kbd>
              </span>
            </button>
          )}
          {!isMobile && (desktopSearchOpen || !!searchValue) && (
            <div
              data-testid="header-search-box"
              style={{ position: 'relative', flex: `0 1 ${searchWidth}`, minWidth: '10rem' }}
              ref={searchBoxRef}
              // A press on a result or the ⌘K chip keeps focus in the box, so it
              // does not fold away before the click lands.
              onMouseDown={(e) => {
                if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
              }}
            >
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  aria-label="Search"
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by pressing Search
                  autoFocus
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      refocusSearchButton.current = true;
                      setSearchValue('');
                      setDesktopSearchOpen(false);
                    }
                    handleSearchKeyDown(e);
                  }}
                  onBlur={(e) => {
                    // Folds back into the button when left empty.
                    if (!e.currentTarget.value) setDesktopSearchOpen(false);
                  }}
                  placeholder={GLOBAL_SEARCH_HINT}
                  style={{
                    width: '100%',
                    height: searchHeight,
                    padding: '0 4.5rem 0 0.75rem',
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: '0.5rem',
                    color: theme.colors.text.primary,
                    fontSize: '0.75rem',
                    outline: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    color: theme.colors.text.secondary,
                  }}
                >
                  <Kbd
                    onClick={() => window.dispatchEvent(new CustomEvent('openCommandPalette'))}
                    title="Open command palette"
                    style={{ color: theme.colors.text.tertiary }}
                  >
                    {IS_MAC ? '⌘K' : 'Ctrl K'}
                  </Kbd>
                  {searchLoading ? (
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        border: '2px solid var(--tc-border-light, #333333)',
                        borderTop: '2px solid var(--tc-border-focus, #007AFF)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                  ) : (
                    customSearchIcon || <Search size={16} />
                  )}
                </div>
              </div>

              {/* Search Suggestions */}
              {openSuggestions && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    backgroundColor: theme.colors.background.secondary,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: 8,
                    maxHeight: 400,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    zIndex: 1001,
                  }}
                  className="search-results-scrollbar"
                >
                  {suggestions.length === 0 && !searchLoading && !settingsLoading ? (
                    <div style={{ padding: 16, color: theme.colors.text.secondary, fontSize: 12, textAlign: 'center' }}>
                      No results found
                    </div>
                  ) : (
                    suggestions.map((item, idx) => {
                      if (item.type === 'settings') {
                        return (
                          <SettingsSearchRow
                            key={item.data.key}
                            hit={item.data}
                            highlighted={idx === highlightIndex}
                            divider={idx < suggestions.length - 1}
                            onPick={() => handleSuggestionClick(item)}
                            onHover={() => setHighlightIndex(idx)}
                          />
                        );
                      }
                      if (item.type === 'work_order') {
                        const wo = item.data;
                        const itemIsDeleted = !!(wo.is_deleted || wo.deleted_at);
                        const displayStatus = itemIsDeleted ? 'deleted' : wo.status;
                        return (
                          <div
                            key={`wo-${wo.id}`}
                            onClick={() => handleSuggestionClick(item)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              backgroundColor: idx === highlightIndex ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent',
                              borderBottom: idx < suggestions.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                              opacity: itemIsDeleted ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
                              setHighlightIndex(idx);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                idx === highlightIndex ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <FileText size={14} style={{ color: theme.colors.status.info }} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.primary }}>
                                {wo.work_order_display_id || wo.name || `WO-${wo.id?.slice(0, 8)}`}
                              </span>
                              {wo.department_name && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: theme.colors.text.secondary,
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: 999,
                                    padding: '1px 8px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {wo.department_name}
                                </span>
                              )}
                              {displayStatus && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    backgroundColor: getStatusColor(displayStatus).bg,
                                    color: getStatusColor(displayStatus).text,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                  }}
                                >
                                  {itemIsDeleted ? 'Deleted' : formatStatusLabel(displayStatus)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }
                      if (item.type === 'customer') {
                        const c = item.data;
                        return (
                          <div
                            key={`customer-${c.id}`}
                            onClick={() => handleSuggestionClick(item)}
                            data-testid={`search-customer-${c.id}`}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              backgroundColor: idx === highlightIndex ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent',
                              borderBottom: idx < suggestions.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                            }}
                            onMouseEnter={() => setHighlightIndex(idx)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <Users size={14} style={{ color: theme.colors.brand.primary, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.primary }}>{c.name}</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: theme.colors.text.secondary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {customerSearchDetail(c, `Customer · ${CUSTOMER_KIND_LABELS[c.kind] ?? c.kind}`)}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      // invoice_batch
                      const batch = item.data;
                      return (
                        <div
                          key={`batch-${batch.id}`}
                          onClick={() => handleSuggestionClick(item)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            backgroundColor: idx === highlightIndex ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent',
                            borderBottom: idx < suggestions.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
                            setHighlightIndex(idx);
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              idx === highlightIndex ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))' : 'transparent';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Receipt size={14} style={{ color: theme.colors.status.warning }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.primary }}>
                              {batch.display_id || `Batch ${batch.id.slice(0, 8)}`}
                            </span>
                            <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>Invoice</span>
                            {batch.status && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  backgroundColor: BATCH_STATUS_BADGE.bg,
                                  color: BATCH_STATUS_BADGE.text,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                {formatStatusLabel(batch.status)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI Chat Button */}
          {canUseAIChat && (
            <AIChatButton onClick={() => setShowAIChat(!showAIChat)} isStreaming={aiChatStreaming} size={ICON_SIZE} buttonSize={btnSize} />
          )}

          {/* Notifications */}
          {isActivitiesAvailable && (
            <div style={{ position: 'relative' }} ref={notificationsRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: btnSize,
                  height: btnSize,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: theme.colors.text.primary,
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                aria-label="Notifications"
              >
                <Bell size={ICON_SIZE} />
                {activitiesTotal > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: isMobile ? '0.375rem' : '0.25rem',
                      right: isMobile ? '0.375rem' : '0.25rem',
                      width: '0.5rem',
                      height: '0.5rem',
                      backgroundColor: 'var(--tc-brand-primary, #DA562C)',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div
                  style={{
                    position: isMobile ? 'fixed' : 'absolute',
                    top: isMobile ? 0 : '100%',
                    left: isMobile ? 0 : undefined,
                    right: isMobile ? 0 : 0,
                    bottom: isMobile ? 0 : undefined,
                    marginTop: isMobile ? 0 : 8,
                    width: isMobile ? '100%' : '22.5rem',
                    maxHeight: isMobile ? '100%' : '30rem',
                    backgroundColor: theme.colors.background.secondary,
                    border: isMobile ? 'none' : `1px solid ${theme.colors.border.default}`,
                    borderRadius: isMobile ? 0 : 8,
                    boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: isMobile ? 10000 : 1001,
                  }}
                >
                  {/* Header with close button */}
                  <div
                    style={{
                      padding: isMobile ? '12px 16px' : '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: `1px solid ${theme.colors.border.default}`,
                    }}
                  >
                    <span style={{ fontSize: isMobile ? 18 : 14, fontWeight: 600, color: theme.colors.text.primary }}>Notifications</span>
                    <button
                      onClick={() => setShowNotifications(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: theme.colors.text.secondary,
                        cursor: 'pointer',
                        padding: isMobile ? 8 : 4,
                        minWidth: isMobile ? 44 : 'auto',
                        minHeight: isMobile ? 44 : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <X size={isMobile ? 24 : 16} />
                    </button>
                  </div>
                  {/* Content */}
                  <div style={{ flex: 1, overflowY: 'auto' }} className="activities-list-scrollbar">
                    {(() => {
                      // Activities content
                      if (activitiesLoading) {
                        return <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.secondary }}>Loading...</div>;
                      }
                      if (activitiesError) {
                        return (
                          <div style={{ padding: 24, textAlign: 'center', color: 'var(--tc-brand-primary, #DA562C)' }}>
                            {activitiesError}
                          </div>
                        );
                      }
                      if (activities.length === 0) {
                        return <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.secondary }}>No activities</div>;
                      }
                      return activities.map((activity, idx) => (
                        <div
                          key={activity.id || idx}
                          role="button"
                          tabIndex={0}
                          data-testid="header-activity-row"
                          aria-label={`Open details: ${activity.description || 'activity'}`}
                          onClick={() => {
                            setShowNotifications(false);
                            setOpenedActivity(activity);
                          }}
                          onKeyDown={(e) => {
                            // Only keys pressed on the row itself. The row can
                            // contain a work-order link (a nested button) whose
                            // Enter/Space bubbles here and must not also open
                            // the details.
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowNotifications(false);
                              setOpenedActivity(activity);
                            }
                          }}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: idx < activities.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                          }}
                        >
                          {(() => {
                            const d = describeActivity(activity);
                            return (
                              <>
                                {/* Actor · Action, then the asset as its own
                                    element built from resource_type/resource_id
                                    — not scraped out of the sentence below. */}
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: d.actorKnown ? theme.colors.text.primary : theme.colors.text.secondary,
                                      fontStyle: d.actorKnown ? 'normal' : 'italic',
                                    }}
                                  >
                                    {d.actor}
                                  </span>
                                  <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>{d.action}</span>
                                </div>
                                {d.asset && (
                                  // The asset link is its own target; it must
                                  // not also open the row's detail.
                                  <div style={{ marginTop: 2 }} onClick={(e) => e.stopPropagation()}>
                                    <ResourceLink
                                      resourceType={d.asset.type}
                                      resourceId={d.asset.id}
                                      // Always the presentation label: a real
                                      // name when resolved, otherwise the
                                      // humanized type. Passing null here would
                                      // drop ResourceLink onto its own
                                      // truncated-uuid fallback and put the
                                      // GUIDs straight back.
                                      resourceName={d.asset.label}
                                      locationId={activity.location_id}
                                      onWorkOrderClick={
                                        onSearchSelect
                                          ? (workOrderId) => {
                                              setShowNotifications(false);
                                              onSearchSelect({ type: 'work_order', id: workOrderId });
                                            }
                                          : undefined
                                      }
                                    />
                                  </div>
                                )}
                                <div style={{ fontSize: 12, color: theme.colors.text.primary, marginTop: 2 }}>
                                  {activity.description || d.action}
                                </div>
                              </>
                            );
                          })()}
                          <div
                            style={{
                              fontSize: 11,
                              color: theme.colors.text.secondary,
                              marginTop: 4,
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                            }}
                          >
                            <span>
                              {new Date(activity.created_at).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Work Session Clock - responsive sizing (hidden for system admin, requires work_sessions:create permission) */}
          {!isSystemAdmin && canClockIn && (
            <WorkSessionClock
              size={isMobile ? 'compact' : 'normal'}
              showLabel={!isMobile && !isTablet}
              accountId={accountId}
              locationId={effectiveLocationId}
              bypassGeofence={bypassGeofence}
            />
          )}

          {/* User Dropdown */}
          <UserProfileDropdown
            user={user}
            permissions={permissions}
            onEditProfile={onEditProfile}
            onAdminDashboard={onAdminDashboard}
            onLogout={onLogout}
            onOpenSettings={settingsTarget ? () => openSettings(settingsTarget) : undefined}
            settingsScopeLabel={settingsTarget ? (settingsTarget.kind === 'account' ? 'Account' : 'Location') : undefined}
          />
        </HeaderContainer>
      </header>

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        user={user}
        permissions={permissions}
        onEditProfile={onEditProfile}
        onAdminDashboard={onAdminDashboard}
        onLogout={onLogout}
      />

      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.colors.background.secondary,
            zIndex: theme.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Mobile Search Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              gap: '0.75rem',
              borderBottom: `1px solid ${theme.colors.border.default}`,
            }}
          >
            <button
              onClick={() => {
                setShowMobileSearch(false);
                setSearchValue('');
                setSuggestions([]);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: btnSize,
                height: btnSize,
                backgroundColor: 'transparent',
                border: 'none',
                color: theme.colors.text.primary,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <X size={24} />
            </button>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={GLOBAL_SEARCH_HINT_SHORT}
                autoFocus
                style={{
                  width: '100%',
                  height: btnSize,
                  padding: '0 2.75rem 0 1rem',
                  backgroundColor: theme.colors.background.primary,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '0.5rem',
                  color: theme.colors.text.primary,
                  fontSize: '0.9375rem',
                  outline: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  color: theme.colors.text.secondary,
                }}
              >
                {searchLoading ? (
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      border: '2px solid var(--tc-border-light, #333333)',
                      borderTop: '2px solid var(--tc-border-focus, #007AFF)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                ) : (
                  <Search size={20} />
                )}
              </div>
            </div>
          </div>

          {/* Mobile Search Results */}
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {searchValue.length === 0 ? (
              <div style={{ padding: 24, color: theme.colors.text.secondary, fontSize: 13, textAlign: 'center' }}>
                Start typing to search work orders, invoices, locations or settings
              </div>
            ) : suggestions.length === 0 && !searchLoading && !settingsLoading ? (
              <div style={{ padding: 24, color: theme.colors.text.secondary, fontSize: 13, textAlign: 'center' }}>No results found</div>
            ) : (
              suggestions.map((item) => {
                if (item.type === 'customer') {
                  const c = item.data;
                  return (
                    <div
                      key={`customer-${c.id}`}
                      data-testid={`search-customer-mobile-${c.id}`}
                      onClick={() => {
                        setShowMobileSearch(false);
                        handleSuggestionClick(item);
                      }}
                      style={{ padding: '16px', cursor: 'pointer', borderBottom: `1px solid ${theme.colors.border.default}` }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Users size={16} style={{ color: theme.colors.brand.primary }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>{c.name}</span>
                      </div>
                      <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                        {customerSearchDetail(c, `Customer · ${CUSTOMER_KIND_LABELS[c.kind] ?? c.kind}`)}
                      </div>
                    </div>
                  );
                }
                if (item.type === 'settings') {
                  return (
                    <SettingsSearchRow
                      key={item.data.key}
                      hit={item.data}
                      compact={false}
                      divider
                      onPick={() => {
                        setShowMobileSearch(false);
                        handleSuggestionClick(item);
                      }}
                    />
                  );
                }
                if (item.type === 'work_order') {
                  const wo = item.data;
                  const itemIsDeleted = !!(wo.is_deleted || wo.deleted_at);
                  const displayStatus = itemIsDeleted ? 'deleted' : wo.status;
                  return (
                    <div
                      key={`wo-${wo.id}`}
                      onClick={() => {
                        setShowMobileSearch(false);
                        handleSuggestionClick(item);
                      }}
                      style={{
                        padding: '16px',
                        cursor: 'pointer',
                        borderBottom: `1px solid ${theme.colors.border.default}`,
                        opacity: itemIsDeleted ? 0.7 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileText size={16} style={{ color: theme.colors.status.info }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>
                          {wo.work_order_display_id || wo.name || `WO-${wo.id?.slice(0, 8)}`}
                        </span>
                        {wo.department_name && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: theme.colors.text.secondary,
                              border: `1px solid ${theme.colors.border.default}`,
                              borderRadius: 999,
                              padding: '1px 8px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {wo.department_name}
                          </span>
                        )}
                        {displayStatus && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 4,
                              backgroundColor: getStatusColor(displayStatus).bg,
                              color: getStatusColor(displayStatus).text,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {itemIsDeleted ? 'Deleted' : formatStatusLabel(displayStatus)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
                const batch = item.data;
                return (
                  <div
                    key={`batch-${batch.id}`}
                    onClick={() => {
                      setShowMobileSearch(false);
                      handleSuggestionClick(item);
                    }}
                    style={{
                      padding: '16px',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.colors.border.default}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Receipt size={16} style={{ color: theme.colors.status.warning }} />
                      <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>
                        {batch.display_id || `Batch ${batch.id.slice(0, 8)}`}
                      </span>
                      <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>Invoice</span>
                      {batch.status && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 4,
                            backgroundColor: BATCH_STATUS_BADGE.bg,
                            color: BATCH_STATUS_BADGE.text,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {formatStatusLabel(batch.status)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Activity feed → full detail: who, where, and what changed. */}
      <ActivityDetailModal
        isOpen={openedActivity !== null}
        activity={openedActivity}
        activityId={openedActivity?.id ?? null}
        // The name-resolving detail call is gated on activities:read; without
        // it the row itself is rendered as-is rather than showing an error.
        fetchDetail={isSystemAdmin || hasActivitiesRead ? (id) => adminService.getActivityDetail(id) : undefined}
        onClose={() => setOpenedActivity(null)}
        onWorkOrderClick={
          onSearchSelect
            ? (workOrderId) => {
                setOpenedActivity(null);
                onSearchSelect({ type: 'work_order', id: workOrderId });
              }
            : undefined
        }
      />

      {/* AI Chat Panel */}
      {canUseAIChat && <AIChatPanel isOpen={showAIChat} onClose={() => setShowAIChat(false)} />}
    </>
  );
};

export default GlobalHeader;
